import { startOfDay, subWeeks } from 'date-fns';
import { useMemo } from 'react';

import { useCatalystInsightsList } from '~/hooks/use-catalyst-insights/useCatalystInsights';
import { useEarningsInsightsFilter } from '~/hooks/use-earnings-insights-filter/useEarningsInsightsFilter';
import { parseEarningsInsights } from '~/hooks/use-earnings-insights-filter/utils';
import { useIsEarningsInsightsEnabled } from '~/hooks/use-is-earnings-insights-enabled/useIsEarningsInsightsEnabled';
import { useFilteredInsights } from '~/services/insights/insights-service';
import { useFlags } from '~/stores/use-flags/useFlags';
import { METHOD_SUB_CLS_GROUPING } from '~/widgets/asset-overview-widget/method_sub_cls_grouping';

import { useAssetsTableStore } from '../../useAssetsTableStore';

const EARNINGS_INSIGHT_KEY = 'earnings';
const SCENARIO_INSIGHT_KEY = 'scenario';
const KG_INSIGHT_KEY = 'kg';
const COMPANY_CATEGORY = 'company';

export interface UseInsightsCellDataParams {
  entityTag: string;
}

export const useInsightsCellData = ({
  entityTag,
}: UseInsightsCellDataParams) => {
  const isCatalystInsightsEnabled = useFlags(state =>
    state.getFeatureFlagState('catalysts_company_insights')
  );
  const isEarningsInsightsEnabled = useIsEarningsInsightsEnabled();
  const insightsFilterState = useAssetsTableStore(
    state => state.insightsFilterState
  );

  const fromDate = useMemo(() => startOfDay(subWeeks(new Date(), 1)), []);

  const { data: earningsInsightsData = [] } = useEarningsInsightsFilter({
    body: { entities: [entityTag], from: fromDate.toISOString() },
    isEnabled: isEarningsInsightsEnabled,
  });
  const { data, isLoading } = useFilteredInsights({ entity: [entityTag] });
  const { data: catalystInsights } = useCatalystInsightsList(
    {
      entities: [entityTag],
      categories: [COMPANY_CATEGORY],
      from: fromDate.toISOString(),
    },
    isCatalystInsightsEnabled
  );

  const earningsInsight = useMemo(
    () => parseEarningsInsights(earningsInsightsData),
    [earningsInsightsData]
  );

  const filteredScenarioInsights = useMemo(() => {
    const allAssetInsights = data?.data?.result ?? [];
    const scenarioInsightsFilterState =
      insightsFilterState[SCENARIO_INSIGHT_KEY];

    if (
      Object.values(scenarioInsightsFilterState).every(item => item.checked)
    ) {
      return allAssetInsights;
    }

    return allAssetInsights.filter(insight => {
      const analysisType = METHOD_SUB_CLS_GROUPING[insight.metadata.topic];
      return analysisType
        ? scenarioInsightsFilterState.find(item => item.key === analysisType)
            ?.checked
        : false;
    });
  }, [data?.data?.result, insightsFilterState]);

  const filteredCatalystInsights = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];

    return catalystInsights?.filter(catalystInsight => {
      const analysisType = METHOD_SUB_CLS_GROUPING[catalystInsight.category];
      return analysisType
        ? kgInsightsFilterState.find(item => item.key === analysisType)?.checked
        : false;
    });
  }, [catalystInsights, insightsFilterState]);

  const showEarningsInsight = useMemo(() => {
    const kgInsightsFilterState = insightsFilterState[KG_INSIGHT_KEY];
    return kgInsightsFilterState.find(item => item.key === EARNINGS_INSIGHT_KEY)
      ?.checked;
  }, [insightsFilterState]);

  return {
    isLoading,
    filteredScenarioInsights,
    filteredCatalystInsights,
    earningsInsight: showEarningsInsight ? earningsInsight : undefined,
  };
};
